import { Fragment, memo, useReducer } from 'react';

import { useFormik } from 'formik';
import { SquarePen } from 'lucide-react';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

import { validationSchema } from '../../form';

import css from '../TelegramDropTable.module.css';

import { rewardsService } from 'v2/api/rewardsService';
import { telegramBotService } from 'v2/api/telegramBotService';
import { RewardsFormControl } from 'v2/components/form/rewardsFormControl/RewardsFormControl';
import { Loader } from 'v2/components/loader/Loader';
import { TelegramBotDropModel, TelegramBotDropForm } from 'v2/types/TelegramBotSettingsModel';
import { handleHttpError, mapRewardsToFormRewards } from 'v2/utils';

interface TelegramDropTableEditActionProps {
  drop: TelegramBotDropModel;
  onRevalidate: VoidFunction;
}

export const TelegramDropTableEditAction = memo<TelegramDropTableEditActionProps>(({ drop, onRevalidate }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { data, isLoading } = useSWR(isOpen && ['getRewardsByIds', drop.rewardIds], rewardsService.getRewardsByIds);

  const { trigger, isMutating } = useSWRMutation(
    ['updateTelegramBotDrop', drop.id],
    telegramBotService.updateTelegramBotDrop,
    {
      onSuccess: () => {
        toast.success('The drop has been successfully updated');
        handleClose();
        onRevalidate();
      },
      onError: handleHttpError,
    }
  );

  const formik = useFormik<TelegramBotDropForm>({
    initialValues: {
      chanceToWin: drop.chanceToWin,
      rewards: mapRewardsToFormRewards(data),
    },
    validationSchema,
    onSubmit: (values) => {
      trigger(values);
    },
    enableReinitialize: true,
  });

  const handleClose = () => {
    setOpen();
    formik.resetForm();
  };

  return (
    <Fragment>
      <Tooltip title="Edit" placement="top">
        <IconButton color="warning" onClick={setOpen}>
          <SquarePen width={20} height={20} />
        </IconButton>
      </Tooltip>

      <Dialog open={isOpen} onClose={handleClose} scroll="paper" maxWidth="sm" fullWidth>
        <DialogTitle>Edit Telegram Bot Drop</DialogTitle>

        <DialogContent>
          <form id="telegramDropEditForm" className={css.telegramDropEditForm} onSubmit={formik.handleSubmit}>
            <TextField
              name="chanceToWin"
              value={formik.values.chanceToWin}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={!!formik.errors.chanceToWin}
              helperText={formik.errors.chanceToWin}
              label="Chance to Win, %"
              required
              fullWidth
            />

            {isLoading ? <Loader /> : <RewardsFormControl formik={formik} withCount required />}
          </form>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="error" variant="outlined">
            Cancel
          </Button>

          <Button form="telegramDropEditForm" type="submit" variant="contained" loading={isMutating}>
            Edit
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
});

TelegramDropTableEditAction.displayName = 'TelegramDropTableEditAction';
